import '../../types/index.mjs';
import { checkUnreachable, assert, assertUnreachable } from '../../utils/errors.mjs';
import { pathnameToParts, partsToPathname, slugify } from '../../utils/url.mjs';
import { GENERIC_SITE_IDS, SiteId } from './mapping.mjs';
import { SiteConfigType } from './types.mjs';
import { StoreId } from '../../types/generated.mjs';
import { Language } from '../../types/constants.mjs';
"use strict";
const parseSiteUrl = ({
  siteUrl: [siteId, pathname, searchParams, fragment],
  storeId,
  addSiteConfigReferenceSearchParams,
  language,
  targetSiteConfig,
  context
}) => {
  const {
    baseUrl,
    languages
  } = targetSiteConfig;
  const targetSiteSupportedLanguage =
  // OK to cast as we are verifying `languages` is type of Language[]
  Array.isArray(languages) && languages?.includes(language) ? language : targetSiteConfig.defaultLanguage;
  const url = new URL(baseUrl);
  const pathnameParts = pathnameToParts(pathname);
  const includeStoreIdAndLanguage = !(context && GENERIC_SITE_IDS.includes(context));
  const basePathname = url.pathname.replace(/\/$/, "");
  switch (siteId) {
    case SiteId.Store:
      {
        url.pathname = `${basePathname}${partsToPathname(targetSiteSupportedLanguage ? [targetSiteSupportedLanguage, ...pathnameParts] : pathnameParts
        // Shopify stores
        )}`;
        break;
      }
    case SiteId.Ui:
      {
        url.pathname = `${basePathname}${partsToPathname(targetSiteSupportedLanguage &&
        // Do not add storeId and language if language is not supported
        includeStoreIdAndLanguage ? [slugify(storeId), targetSiteSupportedLanguage, ...pathnameParts] : pathnameParts
        // To "root" page
        )}`;
        break;
      }
    case SiteId.Uisp:
      {
        url.pathname = `${basePathname}${partsToPathname(includeStoreIdAndLanguage ? [slugify(storeId), ...pathnameParts] : pathnameParts
        // To "root" page
        )}`;
        break;
      }
    case SiteId.Amplifi:
    case SiteId.TechSpecs:
    case SiteId.CaseStudies:
    case SiteId.ExpertsUi:
    case SiteId.Blog:
    case SiteId.ExpertsUisp:
      {
        url.pathname = `${basePathname}${pathname}`;
        break;
      }
    case SiteId.Uwc:
      {
        url.pathname = `${basePathname}${partsToPathname(targetSiteSupportedLanguage ? [targetSiteSupportedLanguage, ...pathnameParts] : pathnameParts)}`;
        break;
      }
    default:
      {
        checkUnreachable(siteId);
        url.pathname = `${basePathname}${pathname}`;
        break;
      }
  }
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value != null) {
        url.searchParams.set(key, value.toString());
      }
    }
  }
  if (addSiteConfigReferenceSearchParams) {
    if (storeId) {
      url.searchParams.set("s", storeId);
    }
    if (targetSiteSupportedLanguage) {
      url.searchParams.set("l", targetSiteSupportedLanguage);
    }
  }
  if (fragment !== void 0) {
    url.hash = slugify(fragment.toString());
  }
  return url.toString();
};
const getSubItemUrl = (subItem, isSubitemUrlActive) => {
  if (!subItem.url) {
    const urls = (subItem.items ?? []).map(item => getSubItemUrl(item, isSubitemUrlActive)).filter(el => !!el);
    const activeUrl = urls.find(isSubitemUrlActive);
    return activeUrl ?? urls[0] ?? null;
  }
  return subItem.url ?? null;
};
const isPartOfUrlPathname = (haystackPathname, needlePathname) => {
  const normalisedHaystackPathname = ensureTrailingSlash(haystackPathname);
  const normalisedNeedlePathname = ensureTrailingSlash(needlePathname);
  return normalisedHaystackPathname.includes(normalisedNeedlePathname);
};
const isUrlPathname = (haystackPathname, needlePathname) => {
  return removeStoreIdAndLanguage(ensureTrailingSlash(haystackPathname)) === removeStoreIdAndLanguage(ensureTrailingSlash(needlePathname));
};
const removeStoreIdAndLanguage = pathname => {
  const parts = pathnameToParts(pathname);
  if (Object.values(StoreId).includes(parts[0])) {
    parts.shift();
  }
  if (Object.values(Language).includes(parts[0])) {
    parts.shift();
  }
  return partsToPathname(parts);
};
const ensureTrailingSlash = pathname => pathname.replace(/\/$/, "") + "/";
const findFirstPossibleDeepNestedItemUrl = items => {
  return items.find(item => item.possible)?.url;
};
const filterOutUnrenderableItems = items => {
  return items.filter(item => !item.invisible);
};
var LanguageDirectionType = /* @__PURE__ */(LanguageDirectionType2 => {
  LanguageDirectionType2["ltr"] = "ltr";
  LanguageDirectionType2["rtl"] = "rtl";
  return LanguageDirectionType2;
})(LanguageDirectionType || {});
const getLanguageDirection = language => {
  return language === Language.Ar ? "rtl" /* rtl */ : "ltr" /* ltr */;
};
const getGlobalSiteConfig = (globalSiteConfig, siteId, storeId) => {
  const siteConfig = globalSiteConfig[siteId];
  assert(!!siteConfig, "Missing SiteId in globalSiteConfig");
  switch (siteConfig.type) {
    case SiteConfigType.Regional:
      return siteConfig.regions[storeId] ?? siteConfig.defaultRegion;
    case SiteConfigType.Basic:
      return siteConfig;
    default:
      assertUnreachable(siteConfig);
  }
};
export { LanguageDirectionType, ensureTrailingSlash, filterOutUnrenderableItems, findFirstPossibleDeepNestedItemUrl, getGlobalSiteConfig, getLanguageDirection, getSubItemUrl, isPartOfUrlPathname, isUrlPathname, parseSiteUrl };
//# sourceMappingURL=utils.mjs.map
